import React, { useEffect, useState } from 'react';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import TableFunction from '../../../../../components/table/TableFunction';
import { useTranslation } from 'react-i18next';
import { GetHidRecordDetails } from '../../../../../reduxToolkit/HID/HidApi';
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { TABLES } from "../../../../../Apis/Tables";
import {EMPTY_VALUES, errorMsg, getValueByKey} from "../../../../../Apis/functions";
import { DEVICE_TYPE, SIOS_TYPE } from "../../SIOs/Model/Models";
import {CONNECTION_TYPE_MODEL, INPUT_DATA, INPUT_NUMBER_DATA} from "../../Inputs/Model/Models";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";

/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * InputTableApp handles displaying and managing the Inputs table for a specific controller or SIO.
 * It supports selection, deletion, and modal creation for new inputs.
 *
 * @param {Array} defaultSioList - Optional default list of SIOs with their inputs to initialize the table.
 *
 * @returns {Object} - Table handlers, selection state, modal controls, and CRUD functions.
 */
const InputTableApp = ({ defaultSioList }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();

    const [tableId, setTableId] = useState("tbl_itc_table");
    const [deleteInputShow, setDeleteInputShow] = useState(false);
    const [showCreateInputModal, setShowCreateInputModal] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [results, setResults] = useState(null);
    const [modified, setModified] = useState(false);

    /**
     * Initializes table data if defaultSioList is provided.
     */
    useEffect(() => {
        if (defaultSioList?.length > 0) {
            const aux = [];
            defaultSioList.forEach((row) => {
                row.inputs.forEach((e) => aux.push(INPUT_DATA.setToTable(e, row.name)));
            });
            setResults({ content: aux, totalElements: aux.length });
        }
    }, [defaultSioList]);

    /**
     * Builds the table header (thead) for the Inputs table.
     *
     * @returns {Array<JSX.Element>} Array of <CustomTh> elements for column headers.
     */
    const getThead = () => [
        <CustomTh tableId={tableId} className='first_head'>{t('name')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('input_number')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('sio')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('sio_type')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('device_type')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('connection_type')}</CustomTh>
    ];

    /**
     * Builds the table body (tbody) displaying inputs data.
     *
     * @returns {Object} Object containing JSX body and raw data.
     */
    const getTbody = () => {
        if (!results || !results?.content || results?.content?.length === 0) return { body: [], data: [] };

        const body = results?.content.map((item) => (
            <>
                <CustomTd tableId={tableId} className='first-column'>{item?.name}</CustomTd>
                <CustomTd tableId={tableId}>{getValueByKey(item?.number, 'name', INPUT_NUMBER_DATA)}</CustomTd>
                <CustomTd tableId={tableId}>{item?.hidSioName}</CustomTd>
                <CustomTd tableId={tableId}>{t(getValueByKey(item?.sioType, 'name', SIOS_TYPE))}</CustomTd>
                <CustomTd tableId={tableId}>{getValueByKey(item?.deviceType, 'name', DEVICE_TYPE)}</CustomTd>
                <CustomTd tableId={tableId}>{t(CONNECTION_TYPE_MODEL[item.typeConnection]?.trans) || EMPTY_VALUES.DEFAULT}</CustomTd>
            </>
        ));

        return { body, data: results?.content };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        dispatch(GetHidRecordDetails({ id })).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                const aux = [];
                data?.sios.forEach((row) => {
                    row.inputs.forEach((e) => aux.push(INPUT_DATA.setToTable(e, row.name)));
                });
                setResults({ content: aux, totalElements: aux.length });
            }
        });
    }

    /**
     * Table utility hook to handle selection, rendering, and delete operations.
     */
    const { thead, tbody, handleCheckboxChange, handleDeleteAll } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getTbody,
        getThead,
        setIsAllChecked,

        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Deletes selected inputs from the backend and refreshes the table.
     */
    const deleteSelectedInput = () => {
        const body = selectedList;
        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName: TABLES.HID_INPUT, body })).then(({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    setSelectedList([]);
                    setIsAllChecked(false);
                    setModified(true);
                }else errorMsg(code);
            });
        }
    };

    return {
        t,
        id,
        thead,
        tbody,
        tableId,
        selectedList,
        isAllChecked,
        deleteInputShow,
        setDeleteInputShow,
        showCreateInputModal,
        setShowCreateInputModal,
        setModified,
        handleCheckboxChange,
        handleDeleteAll,
        deleteSelectedInput
    };
};

export default InputTableApp;
